import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PhoneCall, Clock, CheckCircle2, MessageSquare, Award, AlertTriangle } from 'lucide-react';
import { usePreferences } from '@/context/PreferencesContext';
import { cn } from '@/lib/utils';

const CallSummaryModal = ({ open, onClose, summary, onSeeSubscriptions }) => {
    const { theme, fetchPreferences } = usePreferences();
    const isDark = theme !== 'light';

    const handleClose = () => {
        try {
            fetchPreferences();
        } catch (_) {}
        if (onClose) onClose();
    };

    if (!summary) return null;

    const { tutor, durationSeconds, minutesUsed, minutesLeft, chapterName, questions = [] } = summary;

    const formatDuration = (totalSecs) => {
        if (!totalSecs) return '0s';
        if (totalSecs < 60) {
            return `${totalSecs}s`;
        }
        return `${Math.round(totalSecs / 60)} min`;
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
            <DialogContent className="sm:max-w-[460px] border border-[hsl(var(--card-border))] shadow-2xl p-0 overflow-hidden rounded-3xl bg-[hsl(var(--card-bg))] text-foreground">
                {/* Header graphic */}
                <div className="px-6 py-4 max-sm:py-2 text-center border-b border-[hsl(var(--card-border))] flex flex-col items-center justify-center relative bg-[hsl(var(--main-bg))]">
                    <div className="h-14 w-14 rounded-full flex items-center justify-center mb-2 max-sm:mb-0 shadow-md bg-emerald-500/15 text-[hsl(var(--green-bright))] dark:text-emerald-400">
                        <CheckCircle2 className="w-8 h-8" />
                    </div>
                    <DialogTitle className="text-xl font-bold tracking-tight text-foreground">Call Completed</DialogTitle>
                    {tutor?.subject_name && (
                        <div className="flex justify-center mt-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold border bg-emerald-500/15 border-emerald-500/30 text-emerald-600 dark:text-emerald-400">
                                {tutor.subject_name}
                                {chapterName && (
                                    <>
                                        <span className="mx-1 opacity-60">•</span>
                                        {chapterName}
                                    </>
                                )}
                            </span>
                        </div>
                    )}
                </div>

                <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto scrollbar-thin">
                    {/* Tutor info */}
                    <div className="flex items-center gap-3">
                        {tutor?.avatar_url ? (
                            <img
                                src={tutor.avatar_url}
                                alt={tutor.full_name}
                                className="w-10 h-10 rounded-full object-cover border border-[hsl(var(--card-border))]"
                            />
                        ) : (
                            <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] text-foreground">
                                {tutor?.full_name?.charAt(0) || 'T'}
                            </div>
                        )}
                        <div>
                            <h4 className="text-sm font-bold flex items-center gap-1.5 text-foreground">
                                {tutor?.full_name || 'AI Tutor'}
                                <span className="inline-block text-[8px] font-bold border px-1.5 py-0.2 rounded-full text-emerald-600 dark:text-emerald-400 border-emerald-500/40 bg-emerald-500/10">AI</span>
                            </h4>
                            <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                                {tutor?.subject_name || 'Science'} Tutor
                            </p>
                        </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="flex flex-col p-3 rounded-2xl border text-center bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))]">
                            <Clock className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                            <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">Duration</span>
                            <span className="font-bold text-sm mt-1 truncate text-foreground">{formatDuration(durationSeconds)}</span>
                        </div>
                        <div className="flex flex-col p-3 rounded-2xl border text-center bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))]">
                            <Award className="w-4 h-4 mx-auto mb-1 text-[hsl(var(--green-bright))] dark:text-emerald-400" />
                            <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">Mins Left</span>
                            <span className="font-bold text-sm mt-1 truncate text-foreground">{minutesLeft !== undefined ? `${minutesLeft.toFixed(1)} min` : '0.0 min'}</span>
                        </div>
                    </div>

                    {/* Quota Exceeded Warning */}
                    {minutesLeft <= 0 && (
                        <div className={cn(
                            "p-3.5 rounded-2xl border flex items-start gap-2.5 text-xs leading-normal",
                            isDark ? "bg-red-500/10 border-red-500/25 text-red-400" : "bg-red-50 border-red-200 text-red-700"
                        )}>
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 animate-pulse" />
                            <div>
                                <span className="font-bold block mb-0.5">Monthly Quota Exceeded</span>
                                You have used your entire monthly voice call limit. Please upgrade your subscription to continue learning with your AI Tutors.
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter className="p-4 border-t border-[hsl(var(--card-border))] flex flex-col sm:flex-row gap-3 bg-[hsl(var(--main-bg))]">
                    {minutesLeft <= 0 && onSeeSubscriptions ? (
                        <>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleClose}
                                className="w-full sm:w-auto border-[hsl(var(--card-border))] text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--card-bg))]"
                            >
                                Continue Learning...
                            </Button>
                            <Button
                                type="button"
                                onClick={onSeeSubscriptions}
                                className="w-full sm:w-auto ml-auto bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-semibold shadow-lg shadow-emerald-600/20 border-0"
                            >
                                See Subscriptions
                            </Button>
                        </>
                    ) : (
                        <Button
                            type="button"
                            onClick={handleClose}
                            className="w-full bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-semibold shadow-lg shadow-emerald-600/20 py-5 rounded-2xl border-0"
                        >
                            Continue Learning...
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default CallSummaryModal;
